import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import OuterDraggable from "react-draggable";
import Loader from "../../../common/components/Loader";
import { showAlert } from "../../../../config/utils";
import Portal from "../../../common/components/Portal";
import ConfirmationModal from "../../../common/components/ConfirmationModal";
import HeadingConfig from "./HeadingConfig";

const sanitize = key => key.replace(/\s+/g, "_");

export const BbiTableConfig = ({
    fieldsConfig = [],
    isView = false,
    isSkeletonView = false,
    onCancel,
    onSave,
    isInitialclick,
    isBsi = false,
    isBai = false,
    isClientProperty = false
}) => {
    const [isLoading, setLoading] = useState(true);
    const [entries, setEntries] = useState([]);
    const [search, setSearch] = useState("");
    const [transform, setTransform] = useState({ left: 0, top: 0 });
    const [selectedConfig, setSelectedConfig] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const prevConfigRef = useRef();

    const baselineRef = useRef("");
    const escapeHandlerRef = useRef(null);

    useEffect(() => {
        if (!fieldsConfig) return;
        const init = initializeEntries(fieldsConfig);
        if (!prevConfigRef.current || JSON.stringify(init) !== JSON.stringify(prevConfigRef.current)) {
            prevConfigRef.current = init;
            baselineRef.current = JSON.stringify(init);
            setEntries(init);
        }
    }, [fieldsConfig]);

    const requestClose = useCallback(() => {
        if (isView || isSkeletonView) {
            onCancel();
            return;
        }
        if (baselineRef?.current && JSON.stringify(entries) !== baselineRef?.current) {
            setShowCloseConfirm(true);
            return;
        }
        onCancel();
    }, [entries, isView, isSkeletonView, onCancel]);

    useEffect(() => {
        const handleEscape = e => {
            if (e.keyCode !== 27) return;
            if (selectedConfig || showCloseConfirm) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            if (escapeHandlerRef?.current) {
                escapeHandlerRef?.current();
                return;
            }
            requestClose();
        };
        document.addEventListener("keydown", handleEscape, true);
        return () => document.removeEventListener("keydown", handleEscape, true);
    }, [requestClose, selectedConfig, showCloseConfirm]);

    const initializeEntries = config => {
        setLoading(true);
        const list = [];
        const buildingItems = [];
        let buildingBandInserted = false;

        config.forEach(field => {
            if (field.field_type && field.selected_fields) {
                const items = Object.entries(field.selected_fields).map(([key, sf]) => ({
                    id: key,
                    name: sf.label,
                    originial_name: sf.label,
                    fill_cells: sf.fill_cells,
                    vertical_line: sf.vertical_line,
                    selected: sf.is_active,
                    field_type: sf.field_type ?? "dynamic"
                }));
                list.push({
                    type: "band",
                    key: field.field_name,
                    sanitizedKey: sanitize(field.field_name),
                    label: field.label,
                    items,
                    show_heading: field.show_heading,
                    show_separate_table: field?.show_separate_table || false,
                    field_type: field.field_type ?? "dynamic",
                    open: false,
                    config: field?.config || {}
                });
            } else {
                buildingItems.push({
                    id: field.field_name,
                    name: field.label,
                    originial_name: field.label,
                    fill_cells: field.fill_cells,
                    vertical_line: field.vertical_line,
                    selected: field.is_active,
                    field_type: field.field_type ?? "static"
                });

                if (!buildingBandInserted && !isInitialclick) {
                    list.push({
                        type: "band",
                        key: "building_fields",
                        label: isBsi ? "Site Fields" : isBai ? "Addition Fields" : "Building Fields",
                        sanitizedKey: "building_fields",
                        items: [],
                        open: false
                    });
                    buildingBandInserted = true;
                }
            }
        });
        if (buildingItems?.length > 0) {
            const buildingBand = {
                type: "band",
                key: "building_fields",
                sanitizedKey: "building_fields",
                label: isBsi ? "Site Fields" : isBai ? "Addition Fields" : "Building Fields",
                items: buildingItems,
                open: false
            };
            if (isInitialclick) {
                list?.unshift(buildingBand);
            } else {
                const buildingBandIndex = list?.findIndex(e => e?.key === "building_fields");
                if (buildingBandIndex !== -1) {
                    list[buildingBandIndex].items = buildingItems;
                }
            }
        }

        list.forEach((entry, index) => {
            entry.open = index === 0;
        });

        setLoading(false);
        return list;
    };

    const filteredEntries = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return entries;
        return entries
            .filter(entry => {
                if (entry.type === "field") {
                    return entry.name.toLowerCase().includes(q);
                }
                return entry?.items?.some(item => item.name.toLowerCase().includes(q));
            })
            .map(entry => (entry.type === "band" ? { ...entry, items: entry.items.filter(item => item.name.toLowerCase().includes(q)) } : entry));
    }, [entries, search]);

    const handleSelectAll = useCallback(() => {
        const q = search.trim().toLowerCase();
        const select = !(
            filteredEntries.length > 0 && filteredEntries.every(v => (v.type === "field" ? v.selected : v.items.every(i => i.selected)))
        );
        setEntries(prev =>
            prev.map(entry => {
                if (entry.type === "field") {
                    if (q && !entry.name.toLowerCase().includes(q)) return entry;
                    return { ...entry, selected: select };
                }
                const items = entry.items.map(i => {
                    if (q && !i.name.toLowerCase().includes(q)) return i;
                    return { ...i, selected: select };
                });
                return { ...entry, items, show_heading: select };
            })
        );
    }, [filteredEntries, search]);

    const handleItemToggle = useCallback((bandKey, itemId) => {
        setEntries(prev =>
            prev.map(e => {
                if (e.type === "band" && e.sanitizedKey === bandKey) {
                    const items = e.items.map(i => (i.id === itemId ? { ...i, selected: !i.selected } : i));
                    const anySelected = items.some(i => i.selected);
                    return {
                        ...e,
                        items,
                        show_heading: anySelected ? e.show_heading : false,
                        show_separate_table: anySelected ? e.show_separate_table : false
                    };
                }
                return e;
            })
        );
    }, []);
    const handleSaveName = useCallback((newName, itemId, bandKey) => {
        setEntries(prev =>
            prev.map(e => {
                if (e.type === "band" && e?.sanitizedKey === bandKey) {
                    const items = e?.items?.map(i => (i?.id === itemId ? { ...i, name: newName } : i));
                    return { ...e, items };
                }
                return e;
            })
        );
    }, []);

    const handleItemFillChange = useCallback((bandKey, itemId, value) => {
        setEntries(prev =>
            prev.map(e => {
                if (e.type === "band" && e.sanitizedKey === bandKey) {
                    return {
                        ...e,
                        items: e.items.map(i => {
                            if (i.id !== itemId) return i;
                            let n = parseInt(value, 10);
                            if (isNaN(n)) {
                                return { ...i, fill_cells: "" };
                            }
                            n = Math.min(6, Math.max(1, n));
                            return { ...i, fill_cells: n };
                        })
                    };
                }
                return e;
            })
        );
    }, []);

    const handleItemVerticalToggle = useCallback((bandKey, itemId) => {
        setEntries(prev =>
            prev.map(e =>
                e.type === "band" && e.sanitizedKey === bandKey
                    ? { ...e, items: e.items.map(i => (i.id === itemId ? { ...i, vertical_line: !i.vertical_line } : i)) }
                    : e
            )
        );
    }, []);

    const reorder = (list = [], startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const moveItem = (sourceList = [], destList = [], sourceIdx, destIdx) => {
        const sourceClone = Array.from(sourceList);
        const destClone = Array.from(destList);
        const [moved] = sourceClone.splice(sourceIdx, 1);
        destClone.splice(destIdx, 0, moved);
        return { sourceClone, destClone };
    };

    const onDragEnd = useCallback(
        ({ source, destination, type }) => {
            if (!destination) return;

            // if (type === "ITEM" && source.droppableId === "ITEM-building_fields" && destination.droppableId !== "ITEM-building_fields") {
            //     const buildingEntry = entries.find(b => b.sanitizedKey === "building_fields");
            //     const draggedField = buildingEntry?.items?.[source.index];
            //     if (draggedField?.field_type !== "dynamic") {
            //         showAlert("Oops..! You can't drag static field into a dynamic band");
            //         return;
            //     }
            // }
            if (type === "ENTRY") {
                setEntries(prev => reorder(prev, source.index, destination.index));
                return;
            }

            if (type === "ITEM") {
                if (
                    (source.droppableId === "ITEM-additions" && destination.droppableId !== "ITEM-additions") ||
                    (destination.droppableId === "ITEM-additions" && source.droppableId !== "ITEM-additions" && !isClientProperty)
                ) {
                    return;
                }
                const sameList = source.droppableId === destination.droppableId;

                setEntries(prev => {
                    const updated = [...prev];

                    const sourceKey = source.droppableId.replace("ITEM-", "");
                    const destKey = destination.droppableId.replace("ITEM-", "");

                    const bandIdx = prev.findIndex(b => b.sanitizedKey === sourceKey);
                    if (bandIdx < 0) {
                        console.warn("Unknown source:", sourceKey);
                        return prev;
                    }

                    if (sameList) {
                        const items = Array.isArray(updated[bandIdx].items) ? updated[bandIdx].items : [];
                        updated[bandIdx] = {
                            ...updated[bandIdx],
                            items: reorder(items, source.index, destination.index)
                        };
                    } else {
                        const destIdx = prev.findIndex(b => b.sanitizedKey === destKey);
                        if (destIdx < 0) {
                            console.warn("Unknown destination:", destKey);
                            return prev;
                        }

                        const sourceList = Array.isArray(updated[bandIdx].items) ? updated[bandIdx].items : [];
                        const destList = Array.isArray(updated[destIdx].items) ? updated[destIdx].items : [];

                        const { sourceClone, destClone } = moveItem(sourceList, destList, source.index, destination.index);

                        updated[bandIdx] = { ...updated[bandIdx], items: sourceClone };
                        updated[destIdx] = { ...updated[destIdx], items: destClone };
                    }
                    return updated;
                });
            }
        },
        [entries, setEntries]
    );

    const handleSave = useCallback(() => {
        const fieldById = {};
        const selectedById = {};
        fieldsConfig.forEach(f => {
            fieldById[f.field_name] = f;
            if (f.selected_fields) {
                Object.entries(f.selected_fields).forEach(([id, sf]) => {
                    selectedById[id] = sf;
                });
            }
        });

        const getTemplate = id => {
            const template = selectedById[id] || fieldById[id] || { label: id };
            const { selected_fields: _, ...base } = template;
            return base;
        };

        const updatedConfig = [];
        entries.forEach(entry => {
            if (entry.key === "building_fields") {
                entry.items.forEach(item => {
                    const base = getTemplate(item.id);
                    updatedConfig.push({
                        ...base,
                        field_name: item.id,
                        fill_cells: item.fill_cells,
                        vertical_line: item.vertical_line,
                        is_active: item.selected,
                        field_type: item.field_type,
                        label: item?.name
                    });
                });
                return;
            }

            const bandBase = { ...fieldById[entry.key] };
            const selected_fields = {};
            entry.items.forEach((item, order) => {
                const sfBase = getTemplate(item.id);
                selected_fields[item.id] = {
                    ...sfBase,
                    fill_cells: item.fill_cells,
                    vertical_line: item.vertical_line,
                    is_active: item.selected,
                    field_type: item.field_type,
                    order,
                    label: item?.name
                };
            });
            updatedConfig.push({
                ...bandBase,
                show_heading: entry.show_heading,
                show_separate_table: entry?.show_separate_table || false,
                selected_fields,
                is_active: entry.items.some(i => i.selected),
                field_type: entry.field_type,
                config: entry?.config || {}
            });
        });
        if (!isBai && !isClientProperty) {
            const hasInvalid = (updatedConfig || []).some(entry => {
                if (entry.field_type === "additions") {
                    const selectedFields = Object.values(entry?.selected_fields || {}).filter(f => f?.is_active);
                    if (selectedFields.length > 6) {
                        showAlert(`You can select only up to 6 fields in ${isBsi ? "Buildings" : "Additions"}.`);
                        return true;
                    }
                }
                return false;
            });

            if (!hasInvalid) {
                onSave(updatedConfig);
            }
        } else {
            onSave(updatedConfig);
        }
    }, [entries, fieldsConfig, onSave]);

    const handleBandSelectAll = useCallback(bandKey => {
        setEntries(prev =>
            prev.map(e => {
                if (e.type === "band" && e.sanitizedKey === bandKey) {
                    const all = e.items.every(i => i.selected);
                    const items = e.items.map(i => ({ ...i, selected: !all }));
                    const anySelected = items.some(i => i.selected);
                    return { ...e, items, show_heading: anySelected };
                }
                return e;
            })
        );
    }, []);

    const handleShowHeadingToggle = useCallback(bandKey => {
        setEntries(prev => prev.map(e => (e.type === "band" && e.sanitizedKey === bandKey ? { ...e, show_heading: !e.show_heading } : e)));
    }, []);
    const handleShowSeparateTableToggle = useCallback(bandKey => {
        setEntries(prev =>
            prev.map(e =>
                e.type === "band" && e.sanitizedKey === bandKey ? { ...e, show_separate_table: !e.show_separate_table, show_heading: false } : e
            )
        );
    }, []);

    const handleTransform = useCallback((e, ui) => setTransform({ left: ui.x, top: ui.y }), []);

    const allSelected = useMemo(() => {
        if (!filteredEntries.length) return false;
        return filteredEntries.every(v => (v.type === "field" ? v.selected : v.items.every(i => i.selected)));
    }, [filteredEntries]);

    const renderTableConfig = () => {
        if (!selectedConfig) return null;
        const selectedData = entries?.find(e => e?.key === selectedConfig)?.config || {};
        return (
            <Portal
                body={
                    <HeadingConfig
                        onSave={savedData => {
                            setEntries(prev =>
                                prev.map(e => {
                                    if (e.type === "band" && e.key === selectedConfig) {
                                        return {
                                            ...e,
                                            config: savedData
                                        };
                                    }
                                    return e;
                                })
                            );
                            setSelectedConfig(false);
                        }}
                        viewOnly={isView || isSkeletonView}
                        onCancel={() => setSelectedConfig(false)}
                        currentData={selectedData}
                        headingName={selectedConfig}
                    />
                }
                onCancel={() => setSelectedConfig(false)}
            />
        );
    };

    return (
        <div className="modal modal-region modal-view reco-view-mdl column-setting-modal bbi-config-modal" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog" role="document">
                <OuterDraggable handle=".config-draggable" cancel=".not-draggable" onDrag={handleTransform}>
                    <div className="modal-content outerModal_draggable" style={transform}>
                        <div className="modal-header config-draggable d-flex justify-content-between align-items-center" style={{ cursor: "move" }}>
                            <h5 className="modal-title">
                                <div className="txt-hed">{isBsi ? "BSI Table Fields" : isBai ? "BAI Table Fields" : "BBI Table Fields"}</div>
                            </h5>
                            <div className="d-flex align-items-center">
                                <button
                                    type="button"
                                    className="close not-draggable"
                                    onClick={e => {
                                        e.stopPropagation();
                                        requestClose();
                                    }}
                                >
                                    <span aria-hidden="true">
                                        <img src="/img/close.svg" alt="" />
                                    </span>
                                </button>
                            </div>
                        </div>
                        <div className="modal-body region-otr">
                            <div className="global-controls mb-3">
                                <div className="d-flex align-items-end mb-2">
                                    <div className="flex-grow-1 position-relative">
                                        <i
                                            className="fas fa-search position-absolute"
                                            style={{ left: "10px", top: "50%", transform: "translateY(-50%)", color: "#aaa" }}
                                        />
                                        <input
                                            type="text"
                                            className="form-control pl-5"
                                            placeholder="Search Columns"
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <div className="ml-3 mb-1">
                                        <label className="container-check mb-0">
                                            <input
                                                type="checkbox"
                                                disabled={isView || isSkeletonView}
                                                checked={allSelected}
                                                onChange={handleSelectAll}
                                            />
                                            <span className={`checkmark ${isView || isSkeletonView ? "cursor-disabled" : ""}`}></span>
                                            Select All
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {isLoading ? (
                                <div className="col-md-12 p-5 box-otr box-wide" style={{ height: 450 }}>
                                    <Loader />
                                </div>
                            ) : !filteredEntries?.length ? (
                                <div className="col-md-12 box-otr box-wide mb-2 draggable-item">
                                    <div className="rem-txt d-flex align-items-center">
                                        <label className="container-check">No Columns Found</label>
                                    </div>
                                </div>
                            ) : (
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="entries" type="ENTRY">
                                        {provided => (
                                            <div ref={provided.innerRef} {...provided.droppableProps} className="bbi-table-config-container">
                                                {filteredEntries.map((entry, idx) => (
                                                    <Draggable
                                                        key={`band-${entry.sanitizedKey}`}
                                                        draggableId={`band-${entry.sanitizedKey}`}
                                                        index={idx}
                                                    >
                                                        {pm => (
                                                            <div ref={pm.innerRef} {...pm.draggableProps} className="card p-0 mb-2">
                                                                <div className="card-header d-flex align-items-center" {...pm.dragHandleProps}>
                                                                    <div className="d-flex content-block-2 otr-recom-div no-bottom-bord btn align-items-center">
                                                                        <div className="image-drag mr-2">
                                                                            <img src="/img/Group 2.svg" alt="" />
                                                                        </div>
                                                                        <button
                                                                            className="btn heading-btn-accordion"
                                                                            data-toggle="collapse"
                                                                            data-target={`#collapse-${entry.sanitizedKey}`}
                                                                            aria-expanded="false"
                                                                            aria-controls={`collapse-${entry.sanitizedKey}`}
                                                                        >
                                                                            {entry.label}
                                                                        </button>
                                                                        <div className="col-md-12 box-otr box-wide total-summary p-2">
                                                                            {entry.key !== "building_fields" && entry.key !== "additions" && (
                                                                                <>
                                                                                    <label className="container-check mr-1">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            disabled={entry.items.every(i => !i.selected)}
                                                                                            checked={entry.show_separate_table}
                                                                                            onChange={() =>
                                                                                                handleShowSeparateTableToggle(entry.sanitizedKey)
                                                                                            }
                                                                                        />
                                                                                        <span className="checkbox-label-text">
                                                                                            Show as a Separate Table
                                                                                        </span>
                                                                                        <span
                                                                                            className={`checkmark ${
                                                                                                entry.items.every(i => !i.selected)
                                                                                                    ? "cursor-disabled"
                                                                                                    : ""
                                                                                            }`}
                                                                                        />
                                                                                    </label>
                                                                                    <label className="container-check mr-1 ml-1">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            disabled={
                                                                                                entry.items.every(i => !i.selected) ||
                                                                                                entry.show_separate_table
                                                                                            }
                                                                                            checked={entry.show_heading}
                                                                                            onChange={() =>
                                                                                                handleShowHeadingToggle(entry.sanitizedKey)
                                                                                            }
                                                                                        />{" "}
                                                                                        <span className="checkbox-label-text">Show Prefix</span>
                                                                                        <span
                                                                                            className={`checkmark ${
                                                                                                entry.items.every(i => !i.selected) ||
                                                                                                entry.show_separate_table
                                                                                                    ? "cursor-disabled"
                                                                                                    : ""
                                                                                            }`}
                                                                                        />
                                                                                    </label>
                                                                                </>
                                                                            )}
                                                                            {entry.key !== "additions" && (
                                                                                <label className="container-check">
                                                                                    Select All ({entry.items.filter(i => i.selected).length}/
                                                                                    {entry.items.length})
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        disabled={isView || isSkeletonView}
                                                                                        checked={
                                                                                            entry.items.length > 0 &&
                                                                                            entry.items.every(i => i.selected)
                                                                                        }
                                                                                        onChange={() => handleBandSelectAll(entry.sanitizedKey)}
                                                                                    />
                                                                                    <span
                                                                                        className={`checkmark ${
                                                                                            isView || isSkeletonView ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                    ></span>
                                                                                </label>
                                                                            )}
                                                                            {entry.key !== "building_fields" && entry.key !== "additions" && (
                                                                                <div className={`check-box-area mr- bbi-dynamic-config-btn `}>
                                                                                    <button
                                                                                        className={`btn btn-primary ${
                                                                                            !entry?.show_separate_table ? "cursor-disabled" : ""
                                                                                        }`}
                                                                                        disabled={!entry?.show_separate_table}
                                                                                        onClick={() => {
                                                                                            setSelectedConfig(entry.key);
                                                                                        }}
                                                                                    >
                                                                                        Configure
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    id={`collapse-${entry.sanitizedKey}`}
                                                                    className={`collapse ${entry.open ? "show" : ""}`}
                                                                >
                                                                    <Droppable droppableId={`ITEM-${entry.sanitizedKey}`} type="ITEM">
                                                                        {provided2 => (
                                                                            <div
                                                                                ref={provided2.innerRef}
                                                                                {...provided2.droppableProps}
                                                                                className="p-2"
                                                                            >
                                                                                {entry?.items?.length ? (
                                                                                    entry.items.map((item, idx2) => (
                                                                                        <Draggable
                                                                                            key={`item-${item.id}`}
                                                                                            draggableId={`item-${item.id}`}
                                                                                            index={idx2}
                                                                                        >
                                                                                            {ip => (
                                                                                                <div
                                                                                                    ref={ip.innerRef}
                                                                                                    {...ip.draggableProps}
                                                                                                    {...ip.dragHandleProps}
                                                                                                    className="col-md-12 box-otr box-wide d-flex justify-content-between align-items-center mb-2"
                                                                                                >
                                                                                                    <div className="d-flex align-items-center">
                                                                                                        <div className="image-drag mr-2">
                                                                                                            <img src="/img/Group 2.svg" alt="" />
                                                                                                        </div>
                                                                                                        <EditableName
                                                                                                            escapeRef={escapeHandlerRef}
                                                                                                            value={item?.name}
                                                                                                            originalValue={item?.originial_name}
                                                                                                            disabled={isView || isSkeletonView}
                                                                                                            checked={item.selected}
                                                                                                            onCheck={e => {
                                                                                                                handleItemToggle(
                                                                                                                    entry.sanitizedKey,
                                                                                                                    item.id
                                                                                                                );
                                                                                                            }}
                                                                                                            onSave={newName => {
                                                                                                                handleSaveName(
                                                                                                                    newName,
                                                                                                                    item?.id,
                                                                                                                    entry.sanitizedKey
                                                                                                                );
                                                                                                            }}
                                                                                                        />
                                                                                                    </div>
                                                                                                    <div className="d-flex align-items-center">
                                                                                                        <label className="mr-2 mb-0">
                                                                                                            Fill Cells:
                                                                                                        </label>
                                                                                                        <input
                                                                                                            type="text"
                                                                                                            className="custom-input form-control mr-2"
                                                                                                            style={{ width: "60px" }}
                                                                                                            disabled={
                                                                                                                isView ||
                                                                                                                isSkeletonView ||
                                                                                                                entry.key === "additions"
                                                                                                            }
                                                                                                            value={item.fill_cells}
                                                                                                            inputMode="numeric"
                                                                                                            onChange={e => {
                                                                                                                handleItemFillChange(
                                                                                                                    entry.sanitizedKey,
                                                                                                                    item.id,
                                                                                                                    e.target.value
                                                                                                                );
                                                                                                            }}
                                                                                                            onBlur={e => {
                                                                                                                if (e.target.value === "") {
                                                                                                                    handleItemFillChange(
                                                                                                                        entry.sanitizedKey,
                                                                                                                        item.id,
                                                                                                                        "1"
                                                                                                                    );
                                                                                                                }
                                                                                                            }}
                                                                                                        />
                                                                                                        <label className="mr-2 mb-0">
                                                                                                            Vertical Line:
                                                                                                        </label>
                                                                                                        <input
                                                                                                            type="checkbox"
                                                                                                            disabled={isView || isSkeletonView}
                                                                                                            checked={item.vertical_line}
                                                                                                            onChange={() =>
                                                                                                                handleItemVerticalToggle(
                                                                                                                    entry.sanitizedKey,
                                                                                                                    item.id
                                                                                                                )
                                                                                                            }
                                                                                                        />
                                                                                                    </div>
                                                                                                </div>
                                                                                            )}
                                                                                        </Draggable>
                                                                                    ))
                                                                                ) : (
                                                                                    <div style={{ color: "#999", textAlign: "center" }}>
                                                                                        Drag fields here
                                                                                    </div>
                                                                                )}
                                                                                {provided2.placeholder}
                                                                            </div>
                                                                        )}
                                                                    </Droppable>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            )}
                        </div>
                        <div className="col-md-12 p-0 mt-4 text-right btnOtr d-flex">
                            <div className="tot-btr">
                                <button
                                    type="button"
                                    onClick={e => {
                                        e.stopPropagation();
                                        requestClose();
                                    }}
                                    className="btn btn-secondary btnClr col-md-3 mr-1"
                                >
                                    Cancel
                                </button>
                                {!isView && !isSkeletonView && (
                                    <button type="button" onClick={handleSave} className="btn btn-primary btnRgion btn-wid">
                                        Ok
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </OuterDraggable>
                {renderTableConfig()}
                {showCloseConfirm && (
                    <Portal
                        body={
                            <ConfirmationModal
                                type="cancel"
                                heading="Do you want to clear and lose all changes?"
                                message="This action cannot be reverted, are you sure that you need to cancel?"
                                onNo={() => setShowCloseConfirm(false)}
                                onYes={() => {
                                    setShowCloseConfirm(false);
                                    onCancel();
                                }}
                            />
                        }
                        onCancel={() => setShowCloseConfirm(false)}
                    />
                )}
            </div>
        </div>
    );
};
function EditableName({ value, originalValue, disabled, checked, onCheck, onSave, escapeRef }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const [error, setError] = useState("");
    const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
    const skipBlurRef = useRef(false);

    useEffect(() => {
        if (!editing) {
            setDraft(value);
            setError("");
        }
    }, [value, editing]);

    const startEdit = e => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setEditing(true);
    };

    const confirmEdit = () => {
        if (skipBlurRef.current) {
            skipBlurRef.current = false;
            return;
        }
        if (draft.trim() === "") {
            setError("Name cannot be empty");
            return;
        }
        onSave(draft.trim());
        setEditing(false);
        setError("");
    };

    const cancelEdit = () => {
        skipBlurRef.current = true;
        setEditing(false);
        setDraft(value);
        setError("");
    };

    const handleRestoreClick = e => {
        e.preventDefault();
        e.stopPropagation();
        setShowRestoreConfirm(true);
    };

    const handleConfirmRestore = () => {
        onSave(originalValue);
        setShowRestoreConfirm(false);
    };

    const handleCancelRestore = () => {
        setShowRestoreConfirm(false);
    };

    useEffect(() => {
        if (!escapeRef) return;
        if (!editing && !showRestoreConfirm) return;
        const handler = showRestoreConfirm ? () => setShowRestoreConfirm(false) : cancelEdit;
        escapeRef.current = handler;
        return () => {
            if (escapeRef.current === handler) escapeRef.current = null;
        };
    });

    return (
        <>
            <div className="rem-txt">
                {editing ? (
                    <div className="d-flex align-items-center">
                        <input
                            type="text"
                            className={`form-control ${error ? "is-invalid" : ""}`}
                            value={draft}
                            autoFocus
                            onChange={e => {
                                setDraft(e.target.value);
                                if (e.target.value.trim() !== "") setError("");
                            }}
                            onKeyDown={e => {
                                if (e.key === "Enter") confirmEdit();
                                if (e.key === "Escape") cancelEdit();
                            }}
                            onBlur={confirmEdit}
                        />
                    </div>
                ) : (
                    <label className="container-check">
                        {value}
                        <input type="checkbox" disabled={disabled} checked={checked} onChange={onCheck} />
                        <span className={`checkmark ${disabled ? "cursor-disabled" : ""}`} />
                        {!disabled && (
                            <>
                                <i className="fas fa-pencil-alt ml-4 edit-icon" role="button" title="Rename" onClick={startEdit} />
                                {value !== originalValue && (
                                    <i
                                        className="fas fa-undo ml-4 restore-icon"
                                        role="button"
                                        title="Restore original"
                                        onClick={handleRestoreClick}
                                    />
                                )}
                            </>
                        )}
                    </label>
                )}

                {/* {error && <small className="text-danger d-block mt-1">{error}</small>} */}
            </div>

            {showRestoreConfirm && (
                <Portal
                    body={
                        <ConfirmationModal
                            heading={`Restore “${value}” to “${originalValue}”?`}
                            message="This will discard your custom name and revert to the original."
                            onYes={handleConfirmRestore}
                            onNo={handleCancelRestore}
                            type="cancel"
                        />
                    }
                    onCancel={handleCancelRestore}
                />
            )}
        </>
    );
}
